/**
 * 🦊 **The fox's poses** — T288, decision 134. 🎯 haugur chose the verbs:
 * *pounce, sniff the ground, sit and watch, tail moves* — and it sleeps curled.
 *
 * 🔴 **On top of `poseRig`, never instead of it.** The walk, the look, the lean
 * and the tail wag are the sheep's, unchanged; this adds the poses a sheep does
 * not have. `poseRig` resets every bone it touches to REST first, so everything
 * here is PRE-MULTIPLIED onto that and nothing compounds frame on frame.
 *
 * Two halves, because a skinned mesh has no group for "the whole body":
 *
 * | | |
 * |---|---|
 * | {@link poseFox} | the BONES — legs folded, a curled spine, a wrapped tail, a nose down |
 * | {@link foxCarriage} | the WHOLE BODY — tipped back to sit, lowered to lie, lifted in a leap |
 *
 * ⚠️ **Every angle here is a DIAL judged on a baked picture**
 * (`rigsheet.html?model=/models/fox.glb&pose=sit`), never on a number.
 * 📏 **The metre dials were measured at 0.8 m and scaled ×1.25 with the body**
 * (haugur, 2026-10-04: 25% bigger) — re-measure with `rigsheet.html?length=1`.
 * 📏 Signs were found the same way: a pose that came out backwards is one
 * flipped sign, and the picture is how anybody knows.
 */

import { Quaternion } from 'three';
import { type BoundRig, poseRig, type RigPose } from './sheeprig.ts';

type Bound = BoundRig['neck'][number];

/**
 * 🦶 **How much further a fox's legs swing than the sheep's 17°** — ×1.8, about
 * 31°. 🎯 haugur: *"legs are short, i bearly see them move."* ⚠️ A dial, judged
 * on `fox-poses-sheet.mjs`'s walk strip.
 */
export const FOX_SWING = 1.8;

const spin = new Quaternion();

/** Add a turn about one of the animal's own axes, on top of what the bone has. */
function add(b: Bound | undefined, axis: 'across' | 'up' | 'length', angle: number): void {
  if (b === undefined || angle === 0) return;
  // eslint-disable-next-line security/detect-object-injection -- `axis` is a union of three literal keys, not user input.
  spin.setFromAxisAngle(b[axis], angle);
  b.bone.quaternion.premultiply(spin);
}

/** What a frame asks of a fox, on top of the sheep's walk and look. */
export interface FoxPose extends RigPose {
  /** 👁️ sitting on its haunches, 0 (standing) … 1 */
  readonly sit: number;
  /** 😴 curled up asleep, 0 … 1 */
  readonly curl: number;
  /** 👃 nose to the ground, 0 … 1 */
  readonly sniff: number;
  /** 🐭 how far through a pounce, 0 … 1, or 0 when not pouncing */
  readonly pounce: number;
  /**
   * 🦶 **Where in its stride it is, radians** — the same phase `quadSwing` reads
   * (`π / stride × metres walked`), so the paw lift below keeps time with the
   * swing. 0 for a fox that has not walked.
   */
  readonly stepPhase?: number;
  /** 🦶 how much of a full step, 0 (standing) … 1 (walking or faster) */
  readonly step?: number;
}

/**
 * 📐 **The dials.** Radians unless it says metres. Exported so `rigsheet.html`
 * can try a value in its URL without a rebuild.
 */
export const FOX_BODY = {
  /** 👁️ how far the body tips back to sit */
  sitTilt: 0.38,
  /** 👁️ the hind thigh folds forward under the body */
  sitHip: -1.1,
  /** 👁️ the hind shin folds back */
  sitHock: 1.9,
  /** 😴 each leg's top swings forward under the body */
  curlHip: -1.2,
  /** 😴 and the leg is TUCKED — shrunk into the fur from its root, 1 = gone */
  curlTuck: 0.85,
  /** 😴 the whole spine bends sideways into a C, spread over its bones */
  curlSpine: 1.6,
  /** 😴 the head turns round toward the tail */
  curlHead: 1.2,
  /** 😴 and drops onto the paws */
  curlNose: 1.1,
  /** 😴 the tail wraps round, spread over its bones */
  curlTail: 2.4,
  /**
   * 😴 how far the body sinks to lie on the ground, metres. 📏 MEASURED at the
   * game's 0.8 m: with the legs tucked, 0.24 put the belly 16 cm under the grass.
   */
  curlDrop: 0.1,
  /**
   * 👁️ how far the body is LIFTED to sit, metres. 📏 MEASURED: tipping back about
   * the hind feet sinks the rump 3.5 cm by itself, so it comes back up.
   */
  sitLift: 0.044,
  /** 👃 nose down */
  sniffNeck: 0.8,
  /**
   * 🦶 **How far the joint below the top of a leg folds while that leg swings
   * FORWARD** — the paw lifts and the leg steps instead of sliding. 🎯 haugur,
   * 2026-10-04: *"legs are short, i bearly see them move … looking from afar the
   * fox looks more like floating than walking."* ⚠️ A pendulum leg with a paw
   * that never leaves the ground is exactly what gliding looks like.
   */
  stepLift: 0.9,
  /** 🐭 the leap's height, metres */
  pounceHeight: 0.56,
};

/**
 * 🦴 **Pose the fox's bones.** Called once per fox per frame.
 * ⚠️ `pose.sit` and `pose.curl` should not both be above 0 — the brain is in one
 * row at a time, and the renderer blends between them as it changes.
 */
export function poseFox(rig: BoundRig, pose: FoxPose): void {
  poseRig(rig, pose);
  const F = FOX_BODY;

  /* 🦶 The paw lifts on the way forward. A leg's own angle is
     `sign × sin(phase)` (poseRig), so it is travelling FORWARD — a negative turn
     about the across axis is forward, found on a baked picture — while
     `sign × cos(phase)` is below zero. That half of the stride, the joint below
     the top folds back and the paw comes up off the ground. */
  const step = pose.step ?? 0;
  if (step > 0) {
    const phase = pose.stepPhase ?? 0;
    for (const leg of rig.legs) {
      const sign = leg.fore ? leg.side : -leg.side;
      const lift = Math.max(0, -sign * Math.cos(phase)) * step;
      add(leg.bones[1], 'across', F.stepLift * lift);
      add(leg.bones[2], 'across', -F.stepLift * 0.5 * lift);
    }
  }

  /* 👃 Sniff: the neck bends down from the shoulder. */
  add(rig.neck[0], 'across', F.sniffNeck * pose.sniff);

  /* 👁️ Sit: the body tips back (the carriage does that), so the forelegs are
     turned FORWARD by the same tilt to stay upright under the chest, and the
     hind legs fold under the rump. */
  if (pose.sit > 0) {
    for (const leg of rig.legs) {
      if (leg.fore) add(leg.bones[0], 'across', F.sitTilt * pose.sit);
      else {
        add(leg.bones[0], 'across', F.sitHip * pose.sit);
        add(leg.bones[1], 'across', F.sitHock * pose.sit);
      }
    }
    /* …and the head comes back level, looking at you rather than the sky. */
    add(rig.neck[0], 'across', F.sitTilt * pose.sit);
  }

  /* 😴 Curl: every leg folds, the spine bends into a C, the head turns round
     onto the paws and the tail wraps over the nose. */
  if (pose.curl > 0) {
    const c = pose.curl;
    /* 📏 Every leg swings FORWARD (a negative turn about the across axis —
       found on the baked picture) and is TUCKED: shrunk into the fur from its
       root. 🔴 Bending did not work: this rig's foreleg is five bones starting
       at the neck, and folding it made a spike through the ground. A curled
       fox hides its legs, so they are hidden rather than bent. */
    for (const leg of rig.legs) add(leg.bones[0], 'across', F.curlHip * c);
  }
  /* 😴 The tuck, every frame — set back to 1 when awake, because nothing else
     resets a bone's scale. */
  for (const leg of rig.legs) leg.bones[0]?.bone.scale.setScalar(1 - F.curlTuck * pose.curl);
  if (pose.curl > 0) {
    const c = pose.curl;
    const spine = rig.spine.length;
    for (const b of rig.spine) add(b, 'up', (F.curlSpine * c) / Math.max(1, spine));
    const neck = rig.neck.length;
    for (const b of rig.neck) add(b, 'up', (F.curlHead * c) / Math.max(1, neck));
    add(rig.neck[0], 'across', F.curlNose * c);
    add(rig.neck.at(-1), 'across', F.curlNose * 0.5 * c);
    const tail = rig.tail.length;
    for (const b of rig.tail) add(b, 'up', (F.curlTail * c) / Math.max(1, tail));
  }

  /* 🐭 Pounce: legs tucked in the air, forelegs reaching down at the end. */
  if (pose.pounce > 0) {
    const air = Math.sin(Math.PI * Math.min(1, pose.pounce / 0.75));
    for (const leg of rig.legs) add(leg.bones[0], 'across', (leg.fore ? -0.6 : 0.6) * air);
  }
}

/**
 * 🦊 **The whole body's carriage** — how far it is tipped (radians, positive =
 * nose UP) and lifted (metres) — for the renderer to put on the body's tilt
 * group. Pivots at the hind feet, so a sitting fox stays where it sat.
 */
export function foxCarriage(pose: FoxPose): { tilt: number; lift: number } {
  const F = FOX_BODY;
  let tilt = F.sitTilt * pose.sit;
  let lift = -F.curlDrop * pose.curl + F.sitLift * pose.sit;
  if (pose.pounce > 0) {
    const p = pose.pounce;
    if (p < 0.2) {
      /* crouch, nose up a touch */
      tilt += 0.15 * (p / 0.2);
      lift -= 0.05 * (p / 0.2);
    } else if (p < 0.75) {
      /* the arc: up, then over onto its nose */
      const u = (p - 0.2) / 0.55;
      lift += Math.sin(Math.PI * u) * F.pounceHeight;
      tilt += 0.4 - 1.5 * u;
    } else {
      /* nose in the grass, then back up */
      const u = (p - 0.75) / 0.25;
      tilt += -1.1 * (1 - u);
    }
  }
  return { tilt, lift };
}
